import {useSyncExternalStore} from 'react';

export type AccountBlockedReason = 'ACCOUNT_LOCKED' | 'TENANT_SUSPENDED';
export type AccountBlocked = AccountBlockedReason | null;

let accountBlocked: AccountBlocked = null;
const listeners = new Set<() => void>();

export const getAccountBlocked = (): AccountBlocked => accountBlocked;

export const setAccountBlocked = (next: AccountBlocked): void => {
    if (accountBlocked === next) {
        return;
    }
    accountBlocked = next;
    listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const useAccountBlocked = (): AccountBlocked =>
    useSyncExternalStore(subscribe, getAccountBlocked, getAccountBlocked);

const readCode = (data: unknown): string | undefined => {
    if (typeof data === 'object' && data !== null && 'code' in data) {
        const code = (data as {code: unknown}).code;
        return typeof code === 'string' ? code : undefined;
    }
    return undefined;
};

export const classifyAccountAccessFailure = (status: unknown, data: unknown): AccountBlockedReason | null => {
    const code = readCode(data);
    if (status === 423 && code === 'ACCOUNT_LOCKED') {
        return 'ACCOUNT_LOCKED';
    }
    if (status === 403 && code === 'TENANT_SUSPENDED') {
        return 'TENANT_SUSPENDED';
    }
    return null;
};

export const classifyAccountAccessError = (error: unknown): AccountBlockedReason | null => {
    if (typeof error !== 'object' || error === null) {
        return null;
    }
    const {status, data} = error as {status?: unknown; data?: unknown};
    return classifyAccountAccessFailure(status, data);
};

export const recordAccountBlock = (error: unknown): boolean => {
    const reason = classifyAccountAccessError(error);
    if (!reason) {
        return false;
    }
    setAccountBlocked(reason);
    return true;
};

export const recordAccountBlockResponse = async (response: Response): Promise<void> => {
    if (response.status !== 403 && response.status !== 423) {
        return;
    }
    try {
        const data: unknown = await response.clone().json();
        const reason = classifyAccountAccessFailure(response.status, data);
        if (reason) {
            setAccountBlocked(reason);
        }
    } catch {
        return;
    }
};

export const resetAccountAccessStateForTests = (): void => {
    accountBlocked = null;
    listeners.clear();
};
